<template>
  <v-dialog v-model="dialogShow" max-width="640" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-card-title>
        Run simulation
        <v-spacer />
        <v-btn icon aria-label="Close" @click="dialogShow = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- 3D (Gmsh + NGSolve): domínio da frequência, sem evolução no tempo -->
      <v-card-text v-if="is3d">
        <p class="text-body-2">
          The 3D solver (finite elements) computes the steady field of the sources at a single
          wavelength. The results are always: the field on a cut through the geometries and a
          3D view with the energy flow (Poynting vector).
        </p>
        <v-select
          v-model="meshQuality"
          :items="qualityOptions"
          outlined
          dense
          label="Accuracy (mesh and solver)"
          :hint="qualityHints[meshQuality]"
          persistent-hint
        />
        <v-alert dense text type="info" class="mt-4 mb-0 text-body-2">
          There is no video in 3D: the solver works in the frequency domain, so there is no
          time evolution to record. Use the 2D simulator to watch the wave propagate.
        </v-alert>
      </v-card-text>

      <!-- 2D (Meep, FDTD) -->
      <v-card-text v-else>
        <p class="text-subtitle-2 mb-1">Pictures of the field</p>
        <v-checkbox
          v-model="_generateDielectric"
          dense
          hide-details
          class="mt-0"
          label="Structure map (ε)"
        />
        <p class="text-caption ml-8 mb-2">Shows where each material is. Good to check the geometry.</p>
        <v-checkbox v-model="_generateAxis" dense hide-details class="mt-0" label="Field map at the end of the run" />
        <p class="text-caption ml-8 mb-2">Snapshot of the field after the time set in Cell Properties.</p>
        <v-select
          v-model="_plotAxis"
          :items="fieldComponents"
          outlined
          dense
          label="Field component"
          hint="Use the component excited by the source: Ez for Ez (TM) sources, Hz for Hz (TE) sources"
          persistent-hint
          class="mt-2"
        />

        <p class="text-subtitle-2 mt-5 mb-1">Video and labels</p>
        <v-checkbox v-model="_generateVideo" dense hide-details class="mt-0" label="Video of the field" />
        <p class="text-caption ml-8 mb-2">
          One frame per time unit until the end of the run (slower for long times or fine resolution).
        </p>
        <v-checkbox v-model="_labels" dense hide-details class="mt-0" label="Labels" />
        <p class="text-caption ml-8 mb-2">
          Names of sources and outputs on the video and on the maps of the results page.
        </p>

        <p class="text-subtitle-2 mt-5 mb-1">While it runs</p>
        <v-checkbox v-model="_realtime" dense hide-details class="mt-0" label="Live preview (real time)" />
        <p class="text-caption ml-8 mb-2">
          Shows the field over the structure every few seconds while Meep runs, including the runs
          used for the spectra.
        </p>

        <v-alert v-if="hasOutputs" dense text type="info" class="mt-3 mb-0 text-body-2">
          Spectra and resonances are computed automatically for the outputs you placed
          ({{ outputsCount }}).
        </v-alert>
        <v-alert v-else-if="!_generateDielectric && !_generateAxis && !_generateVideo" dense text type="warning" class="mt-3 mb-0 text-body-2">
          Nothing to compute: choose at least one picture, the video or add an output.
        </v-alert>
        <p class="text-caption mt-3 mb-0">
          Reference:
          <a
            href="https://meep.readthedocs.io/en/latest/Python_User_Interface/#data-visualization"
            target="_blank"
            rel="noopener noreferrer"
          >Meep data visualization</a>
        </p>
      </v-card-text>

      <!-- Só administradores: escolher as máquinas ou comparar a latência (o servidor confere o login) -->
      <AdminRunPanel v-if="role.isAdmin && dialogShow" :is3d="is3d" />

      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="dialogShow = false">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="(!is3d && !hasOutputs && !_generateDielectric && !_generateAxis && !_generateVideo) || adminBlocked"
          @click="run"
        >
          <v-icon left>mdi-play</v-icon>
          {{ adminRun.mode === "compare" ? "Run comparison" : "Run" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { role, loadRole } from "@/common/role";
import { adminRun } from "@/common/adminRun";
import { runBenchmark } from "@/common/benchmark";

export default {
  name: "PlotOptions",
  components: {
    AdminRunPanel: () => import("@/components/AdminRunPanel")
  },
  props: {
    is3d: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    e6: 1,
    tabs: 0,
    fieldComponents: [
      { value: "Ez", text: "Ez (TM)" },
      { value: "Hz", text: "Hz (TE)" },
      { value: "Ex", text: "Ex" },
      { value: "Ey", text: "Ey" },
      { value: "Hx", text: "Hx" },
      { value: "Hy", text: "Hy" }
    ],
    qualityOptions: [
      { value: "fast", text: "Fast (preview)" },
      { value: "accurate", text: "Accurate (recommended)" },
      { value: "reference", text: "Reference (slow, small scenes)" }
    ],
    qualityHints: {
      fast: "Quick preview (seconds to a minute). Qualitative: errors of 30–60% in the field",
      accurate: "Second-order elements: errors of about 5–20%, usually under 2 minutes",
      reference: "Absorbing PML layer and finer mesh: errors of 3–10%, but 5–20× slower"
    }
  }),
  mounted() {
    loadRole();
  },
  methods: {
    run() {
      if (this.role.isAdmin && adminRun.mode === "compare") {
        const configs = [...adminRun.configs];
        this.dialogShow = false;
        runBenchmark(this.$store.state.simulator, configs, this.is3d);
        return;
      }
      if (this.is3d) this.Run3dSimulation();
      else this.RunSimulation();
    },
    ...mapActions("simulator", [
      "setCurrentGeometryPosY",
      "setCurrentGeometryPosX",
      "openPlotOptions",
      "set_generateAxis",
      "set_generateVideo",
      "set_generateDielectric",
      "set_plotAxis",
      "set_realtime",
      "set_labels",
      "set_plotOptions",
      "setSimulationOption",
      "RunSimulation",
      "Run3dSimulation"
    ])
  },
  computed: {
    role: () => role,
    adminRun: () => adminRun,
    // Escolha manual sem máquina, comparativo sem configuração ou processos demais para as máquinas: nada a rodar
    adminBlocked() {
      if (!this.role.isAdmin) return false;
      if (adminRun.mode !== "auto" && adminRun.overLimit) return true;
      if (adminRun.mode === "manual") return !adminRun.override;
      if (adminRun.mode === "compare") return !adminRun.configs.length;
      return false;
    },
    ...mapGetters("simulator", [
      "runSimulation",
      "showPlotOptions",
      "generateAxis",
      "generateVideo",
      "generateDielectric",
      "plotAxis",
      "realtime",
      "labels",
      "plotViews",
      "plotOptions",
      "FluxList",
      "quality3d"
    ]),
    outputsCount() {
      return (this.FluxList || []).length;
    },
    hasOutputs() {
      return this.outputsCount > 0;
    },
    meshQuality: {
      get() {
        return this.quality3d;
      },
      set(value) {
        this.setSimulationOption(["quality3d", value]);
      }
    },
    //#region plotOptions 1
    _generateAxis: {
      get() {
        return this.generateAxis;
      },
      set(value) {
        this.set_generateAxis(value);
      }
    },
    _generateVideo: {
      get() {
        return this.generateVideo;
      },
      set(value) {
        this.set_generateVideo(value);
      }
    },
    _generateDielectric: {
      get() {
        return this.generateDielectric;
      },
      set(value) {
        this.set_generateDielectric(value);
      }
    },
    _plotAxis: {
      get() {
        return this.plotAxis;
      },
      set(value) {
        this.set_plotAxis(value);
      }
    },
    _realtime: {
      get() {
        return this.realtime;
      },
      set(value) {
        this.set_realtime(value);
      }
    },
    _labels: {
      get() {
        return this.labels;
      },
      set(value) {
        this.set_labels(value);
      }
    },
    //#endregion

    //#region plotOptions 2 - fieldParameters
    field_parameters_interpolation: {
      get() {
        return this.plotOptions.field_parameters.interpolation;
      },
      set(val) {
        this.set_plotOptions(["field_parameters", "interpolation", val]);
      }
    },
    field_parameters_alpha: {
      get() {
        return this.plotOptions.field_parameters.alpha;
      },
      set(val) {
        this.set_plotOptions(["field_parameters", "alpha", val]);
      }
    },
    field_parameters_cmap: {
      get() {
        return this.plotOptions.field_parameters.cmap;
      },
      set(val) {
        this.set_plotOptions(["field_parameters", "cmap", val]);
      }
    },
    //#endregion

    //#region
    monitor_parameters_alpha: {
      get() {
        return this.plotOptions.monitor_parameters.alpha;
      },
      set(val) {
        this.set_plotOptions(["monitor_parameters", "alpha", val]);
      }
    },
    monitor_parameters_color: {
      get() {
        return this.plotOptions.monitor_parameters.color;
      },
      set(val) {
        this.set_plotOptions(["monitor_parameters", "color", val]);
      }
    },
    monitor_parameters_facecolor: {
      get() {
        return this.plotOptions.monitor_parameters.facecolor;
      },
      set(val) {
        this.set_plotOptions(["monitor_parameters", "facecolor", val]);
      }
    },
    monitor_parameters_edgecolor: {
      get() {
        return this.plotOptions.monitor_parameters.edgecolor;
      },
      set(val) {
        this.set_plotOptions(["monitor_parameters", "edgecolor", val]);
      }
    },
    monitor_parameters_linewidth: {
      get() {
        return this.plotOptions.monitor_parameters.linewidth;
      },
      set(val) {
        this.set_plotOptions(["monitor_parameters", "linewidth", val]);
      }
    },
    monitor_parameters_hatch: {
      get() {
        return this.plotOptions.monitor_parameters.hatch;
      },
      set(val) {
        this.set_plotOptions(["monitor_parameters", "hatch", val]);
      }
    },
    monitor_parameters_label_color: {
      get() {
        return this.plotOptions.monitor_parameters.label_color;
      },
      set(val) {
        this.set_plotOptions(["monitor_parameters", "label_color", val]);
      }
    },
    monitor_parameters_label_alpha: {
      get() {
        return this.plotOptions.monitor_parameters.label_alpha;
      },
      set(val) {
        this.set_plotOptions(["monitor_parameters", "label_alpha", val]);
      }
    },
    monitor_parameters_offset: {
      get() {
        return this.plotOptions.monitor_parameters.offset;
      },
      set(val) {
        this.set_plotOptions(["monitor_parameters", "offset", val]);
      }
    },
    //#endregion

    //#region
    source_parameters_color: {
      get() {
        return this.plotOptions.source_parameters.color;
      },
      set(val) {
        this.set_plotOptions(["source_parameters", "color", val]);
      }
    },
    source_parameters_alpha: {
      get() {
        return this.plotOptions.source_parameters.alpha;
      },
      set(val) {
        this.set_plotOptions(["source_parameters", "alpha", val]);
      }
    },
    source_parameters_facecolor: {
      get() {
        return this.plotOptions.source_parameters.facecolor;
      },
      set(val) {
        this.set_plotOptions(["source_parameters", "facecolor", val]);
      }
    },
    source_parameters_edgecolor: {
      get() {
        return this.plotOptions.source_parameters.edgecolor;
      },
      set(val) {
        this.set_plotOptions(["source_parameters", "edgecolor", val]);
      }
    },
    source_parameters_linewidth: {
      get() {
        return this.plotOptions.source_parameters.linewidth;
      },
      set(val) {
        this.set_plotOptions(["source_parameters", "linewidth", val]);
      }
    },
    source_parameters_hatch: {
      get() {
        return this.plotOptions.source_parameters.hatch;
      },
      set(val) {
        this.set_plotOptions(["source_parameters", "hatch", val]);
      }
    },
    source_parameters_label_color: {
      get() {
        return this.plotOptions.source_parameters.label_color;
      },
      set(val) {
        this.set_plotOptions(["source_parameters", "label_color", val]);
      }
    },
    source_parameters_label_alpha: {
      get() {
        return this.plotOptions.source_parameters.label_alpha;
      },
      set(val) {
        this.set_plotOptions(["source_parameters", "label_alpha", val]);
      }
    },
    source_parameters_offset: {
      get() {
        return this.plotOptions.source_parameters.offset;
      },
      set(val) {
        this.set_plotOptions(["source_parameters", "offset", val]);
      }
    },

    //#endregion

    //#region

    boundary_parameters_alpha: {
      get() {
        return this.plotOptions.boundary_parameters.alpha;
      },
      set(val) {
        this.set_plotOptions(["boundary_parameters", "alpha", val]);
      }
    },
    boundary_parameters_facecolor: {
      get() {
        return this.plotOptions.boundary_parameters.facecolor;
      },
      set(val) {
        this.set_plotOptions(["boundary_parameters", "facecolor", val]);
      }
    },
    boundary_parameters_edgecolor: {
      get() {
        return this.plotOptions.boundary_parameters.edgecolor;
      },
      set(val) {
        this.set_plotOptions(["boundary_parameters", "edgecolor", val]);
      }
    },
    boundary_parameters_linewidth: {
      get() {
        return this.plotOptions.boundary_parameters.linewidth;
      },
      set(val) {
        this.set_plotOptions(["boundary_parameters", "linewidth", val]);
      }
    },
    boundary_parameters_hatch: {
      get() {
        return this.plotOptions.boundary_parameters.hatch;
      },
      set(val) {
        this.set_plotOptions(["boundary_parameters", "hatch", val]);
      }
    },

    //#endregion

    eps_parameters_alpha: {
      get() {
        return this.plotOptions.eps_parameters.alpha;
      },
      set(val) {
        this.set_plotOptions(["eps_parameters", "alpha", val]);
      }
    },
    eps_parameters_interpolation: {
      get() {
        return this.plotOptions.eps_parameters.interpolation;
      },
      set(val) {
        this.set_plotOptions(["eps_parameters", "interpolation", val]);
      }
    },
    eps_parameters_cmap: {
      get() {
        return this.plotOptions.eps_parameters.cmap;
      },
      set(val) {
        this.set_plotOptions(["eps_parameters", "cmap", val]);
      }
    },
    _plotViews: {
      get() {
        return this.plotViews;
      },
      set(val) {
        this.set_plotViews(val);
      }
    },
    _boundary_parameters: {
      get() {
        return this.boundary_parameters;
      },
      set(val) {
        this.set_boundary_parameters(val);
      }
    },
    _eps_parameters: {
      get() {
        return this.eps_parameters;
      },
      set(val) {
        this.set_eps_parameters(val);
      }
    },
    dialogShow: {
      get() {
        return this.showPlotOptions;
      },
      set(val) {
        this.openPlotOptions(val);
      }
    }
  }
};
</script>
